/**
 * The shell's single source of truth for saved-list membership.
 *
 * Ownership sits here for the same reason consent, the domain configuration and
 * telemetry do: every tile in browse, search, news and the detail page renders a
 * heart and a clock, and two pilets holding two copies of the manifest is exactly how
 * the toggles drifted apart before (the `use-persisted-state` failure mode CLAUDE.md
 * warns about). One store, one fetch, every toggle for a title flips together.
 *
 * The store is module scope, not React state, because it is read from inside pilets
 * that are mounted and unmounted independently — a provider would only cover the tree
 * beneath it, and the manifest has to outlive any of them. Components subscribe
 * through `useSyncExternalStore`, so a change reaches every mounted toggle at once.
 */
import * as React from 'react'
import { trackGoal } from '../analytics/plausible'
import { trackError, trackEvent } from '../telemetry/faro'
import { addListItem, fetchListManifest, isSignedOut, removeListItem } from './client'
import { LIST_NAMES } from './types'
import type { ListIdsState, ListManifest, ListMembership, ListName } from './types'

/**
 * Where the manifest is.
 *
 * `anonymous` and `error` are kept apart even though both mean "no memberships to
 * show": the first is a viewer with nothing to save to (the toggles hide), the second
 * is a fault we want reported. Rounding one down to the other is how a signed-in
 * viewer silently loses their lists during an outage.
 */
export type ListManifestStatus = 'anonymous' | 'loading' | 'ready' | 'error'

export interface ListsState {
  status: ListManifestStatus;
  manifest: ListManifest;
  /** In-flight writes, keyed `${list}:${movieId}` — one toggle, one pending flag. */
  pending: ReadonlySet<string>;
}

const emptyManifest = (): ListManifest => {
  const manifest = {} as ListManifest
  LIST_NAMES.forEach(list => { manifest[list] = [] })
  return manifest
}

let state: ListsState = { status: 'anonymous', manifest: emptyManifest(), pending: new Set() }
let request: Promise<void> | undefined

const subscribers = new Set<() => void>()

/**
 * Publish a new snapshot. The state object is replaced rather than mutated —
 * `useSyncExternalStore` compares snapshots by identity, so an in-place edit would be
 * invisible and the toggles would not re-render.
 */
function setState (next: Partial<ListsState>): void {
  state = { ...state, ...next }
  subscribers.forEach(notify => notify())
}

const subscribe = (notify: () => void): (() => void) => {
  subscribers.add(notify)
  return () => { subscribers.delete(notify) }
}

const getState = (): ListsState => state

/**
 * Load the manifest once, after `whoami` has confirmed a session (see AuthProvider).
 *
 * Deliberately *not* part of the startup gate: a viewer whose manifest is slow still
 * gets their app, with the toggles arriving inactive and correcting themselves a
 * moment later. Never rejects — a failure is a state, not an exception, because the
 * only caller is a fire-and-forget effect.
 */
export function loadListManifest (): Promise<void> {
  if (!request) {
    setState({ status: 'loading' })
    request = fetchListManifest()
      .then(manifest => { setState({ status: 'ready', manifest }) })
      .catch((err: unknown) => {
        // A signed-out answer is not a fault: the session went away (or never was),
        // and the toggles should simply have nowhere to write.
        if (isSignedOut(err)) {
          setState({ status: 'anonymous', manifest: emptyManifest() })
          return
        }
        console.warn('[lists] manifest request failed', err)
        trackError(err, { list_operation: 'manifest' })
        setState({ status: 'error', manifest: emptyManifest() })
      })
  }
  return request
}

/**
 * Drop everything and go back to "nobody is signed in" — called when the handshake
 * settles anonymous or fails. Also clears the shared promise, so a later sign-in
 * within the same page load re-fetches rather than replaying the anonymous answer.
 */
export function resetListManifest (): void {
  request = undefined
  setState({ status: 'anonymous', manifest: emptyManifest(), pending: new Set() })
}

/** The whole store, for the rare consumer that wants the status (the list pages). */
export const useLists = (): ListsState =>
  React.useSyncExternalStore(subscribe, getState, getState)

/**
 * Membership of one title in one list — what a toggle renders from.
 *
 * `toggle` is optimistic: it flips the manifest locally, then calls the API and rolls
 * back on failure. A save that stalls behind a round trip reads as broken on a TV
 * remote, which is the whole reason for the local flip.
 */
export function useListMembership (list: ListName, movieId: string): ListMembership {
  const { status, manifest, pending } = useLists()

  const active = manifest[list]?.includes(movieId) ?? false
  const toggle = React.useCallback(
    () => setMembership(list, movieId, !isMember(list, movieId)),
    [list, movieId]
  )

  return {
    active,
    ready: status === 'ready',
    // A viewer whose manifest failed to load still has an account, but writing from a
    // state we know to be wrong would flip the toggle against a list we never read.
    available: status === 'ready' || status === 'loading',
    pending: pending.has(pendingKey(list, movieId)),
    toggle,
  }
}

/**
 * The ids in one list — what a *list page* watches so it can reload its resolved
 * titles when the manifest changes (a title saved from the hero must appear in the
 * favorites row without a refresh).
 *
 * The array identity is stable while that list is unchanged — `applyMembership`
 * replaces only the list it touches — so this is safe to use as an effect dependency,
 * and a write to the *other* list will not re-fire it.
 */
export function useListIds (list: ListName): ListIdsState {
  const { status, manifest } = useLists()
  return {
    ids: manifest[list] ?? [],
    ready: status === 'ready',
    available: status === 'ready' || status === 'loading',
  }
}

/**
 * Add or remove a title, optimistically. Exposed on the pilet API as
 * `addToList`/`removeFromList`, and used by {@link useListMembership}'s `toggle`.
 *
 * Never rejects. A failed write restores the previous membership and reports itself;
 * the caller is an onClick handler, and an unhandled rejection there is a crash.
 */
export async function setMembership (list: ListName, movieId: string, active: boolean): Promise<void> {
  const key = pendingKey(list, movieId)
  // A second click while the first write is in flight would race the rollback, so the
  // toggle's own request is the lock — the button stays live, the write does not.
  if (state.pending.has(key)) return

  const previous = state.manifest[list] ?? []
  applyMembership(list, movieId, active)
  setState({ pending: withKey(state.pending, key) })

  try {
    if (active) {
      await addListItem(list, movieId)
    } else {
      await removeListItem(list, movieId)
    }
    reportMembership(list, movieId, active)
  } catch (err) {
    // Put the list back exactly as it was, order included — an optimistic flip that
    // survives a failed write is a lie the viewer only discovers on their next reload.
    setState({ manifest: { ...state.manifest, [list]: previous } })
    if (isSignedOut(err)) {
      resetListManifest()
      return
    }
    console.warn('[lists] list write failed', err)
    trackError(err, { list_operation: active ? 'add' : 'remove', list_name: list, movie_id: movieId })
  } finally {
    setState({ pending: withoutKey(state.pending, key) })
  }
}

/*
 * Helpers
 */

const pendingKey = (list: ListName, movieId: string): string => `${list}:${movieId}`

const isMember = (list: ListName, movieId: string): boolean =>
  (state.manifest[list] ?? []).includes(movieId)

const withKey = (set: ReadonlySet<string>, key: string): Set<string> => new Set(set).add(key)

const withoutKey = (set: ReadonlySet<string>, key: string): Set<string> => {
  const next = new Set(set)
  next.delete(key)
  return next
}

/** Newest-first, matching the order the manifest itself comes back in. */
function applyMembership (list: ListName, movieId: string, active: boolean): void {
  const current = state.manifest[list] ?? []
  const next = active
    ? (current.includes(movieId) ? current : [movieId, ...current])
    : current.filter(id => id !== movieId)
  setState({ manifest: { ...state.manifest, [list]: next } })
}

/**
 * Where a save is reported, and why it splits.
 *
 * Plausible gets the *rate* — how often people save, on which list — with `list` as a
 * bucketed enum, because that is all its prop budget can carry and all a breakdown of
 * it can usefully show. The **title id** is high-cardinality data about one viewer, so
 * it goes to Faro, where the collector is ours (the same rule the search funnel
 * follows). Both are safe to call with analytics off; nothing here queues.
 */
function reportMembership (list: ListName, movieId: string, active: boolean): void {
  trackGoal(active ? 'List Add' : 'List Remove', { list })
  trackEvent(active ? 'list_add' : 'list_remove', { list_name: list, movie_id: movieId })
}
